package com.evoapps.evoshift.viewmodels

import com.evoapps.evoshift.observability.AnalyticsEvent
import com.evoapps.evoshift.observability.AppTelemetry
import com.evoapps.evoshift.observability.AuthMethod

import evoshift.composeapp.generated.resources.auth_fill_all_fields
import com.evoapps.evoshift.ui.util.UiText
import com.evoapps.evoshift.ui.util.toUiText
import evoshift.composeapp.generated.resources.Res
import evoshift.composeapp.generated.resources.auth_email_not_verified
import evoshift.composeapp.generated.resources.auth_reset_sent
import evoshift.composeapp.generated.resources.auth_verification_sent
import evoshift.composeapp.generated.resources.auth_verify_email
import evoshift.composeapp.generated.resources.error_session_expired
import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import com.evoapps.evoshift.domain.model.User
import com.evoapps.evoshift.domain.usecase.auth.AppleSignInUseCase
import com.evoapps.evoshift.domain.usecase.auth.GoogleSignInUseCase
import com.evoapps.evoshift.domain.usecase.auth.LoginUseCase
import com.evoapps.evoshift.domain.usecase.auth.RegisterDeviceTokenUseCase
import com.evoapps.evoshift.domain.usecase.auth.RegisterUserUseCase
import com.evoapps.evoshift.domain.usecase.auth.ResetPasswordUseCase
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.first
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch
import com.evoapps.evoshift.domain.usecase.auth.CheckEmailVerificationUseCase
import com.evoapps.evoshift.domain.usecase.auth.GetCurrentUserUseCase
import com.evoapps.evoshift.domain.usecase.auth.SendEmailVerificationUseCase

// DEFINICJA STANU UI
data class AuthUiState(
    val isLoading: Boolean = false,
    val user: User? = null,        // Zalogowany użytkownik
    val error: UiText? = null,     // Błąd do wyświetlenia (np. Snackbar)
    val isLoginMode: Boolean = true, // Toggle: Logowanie vs Rejestracja
    val requiresEmailVerification: Boolean = false, // Flaga pokazująca ekran weryfikacji
    val authSuccess: Boolean = false, // Flaga nawigacyjna: Przejdź do Dashboardu
    /** Whether the platform offers Sign in with Apple (SH-92); the button is hidden otherwise. */
    val appleSignInAvailable: Boolean = false
)

// DEFINICJA ZDARZEŃ (AKCJE UŻYTKOWNIKA)
sealed interface AuthEvent {
    data class Login(val email: String, val pass: String) : AuthEvent
    data class Register(val email: String, val pass: String, val name: String) : AuthEvent
    data class ForgotPassword(val email: String) : AuthEvent
    data object ContinueWithGoogle : AuthEvent // Logowanie i rejestracja kontem Google
    data object ContinueWithApple : AuthEvent // Sign in with Apple (SH-92): signs in and registers
    data object CheckEmailVerification : AuthEvent // Sprzawdź czy użytkownik potwierdził
    data object ResendVerificationEmail : AuthEvent // Wyślij ponownie mail
    data object CancelVerification : AuthEvent // Powrót do ekranu logowania
    data object ToggleMode : AuthEvent // Przełącznik "Nie masz konta? Zarejestruj się"
    data object ErrorShown : AuthEvent // Reset błędu po wyświetleniu
}

class AuthViewModel(
    private val loginUseCase: LoginUseCase,
    private val registerUserUseCase: RegisterUserUseCase,
    private val googleSignInUseCase: GoogleSignInUseCase,
    private val appleSignInUseCase: AppleSignInUseCase,
    private val resetPasswordUseCase: ResetPasswordUseCase,
    private val registerDeviceTokenUseCase: RegisterDeviceTokenUseCase,
    private val sendEmailVerificationUseCase: SendEmailVerificationUseCase,
    private val checkEmailVerificationUseCase: CheckEmailVerificationUseCase,
    private val getCurrentUserUseCase: GetCurrentUserUseCase,
    private val deviceInfoProvider: com.evoapps.evoshift.domain.provider.DeviceInfoProvider
) : ViewModel() {

    private val _uiState = MutableStateFlow(AuthUiState(appleSignInAvailable = appleSignInUseCase.isAvailable))
    val uiState: StateFlow<AuthUiState> = _uiState.asStateFlow()

    init {
        checkInitialVerification()
    }

    private fun checkInitialVerification() {
        viewModelScope.launch {
            val user = getCurrentUserUseCase().first()
            if (user != null) {
                val verificationResult = checkEmailVerificationUseCase()
                if (verificationResult.getOrNull() == false) {
                    _uiState.update {
                        it.copy(
                            user = user,
                            requiresEmailVerification = true
                        )
                    }
                }
            }
        }
    }

    fun onEvent(event: AuthEvent) {
        when (event) {
            is AuthEvent.Login -> login(event.email, event.pass)
            is AuthEvent.Register -> register(event.email, event.pass, event.name)
            is AuthEvent.ForgotPassword -> resetPassword(event.email)
            AuthEvent.ContinueWithGoogle -> continueWithGoogle()
            AuthEvent.ContinueWithApple -> continueWithApple()
            AuthEvent.CheckEmailVerification -> checkEmailVerification()
            AuthEvent.ResendVerificationEmail -> resendVerificationEmail()
            AuthEvent.CancelVerification -> cancelVerification()
            AuthEvent.ToggleMode -> {
                _uiState.update { it.copy(isLoginMode = !it.isLoginMode, error = null) }
            }

            AuthEvent.ErrorShown -> {
                _uiState.update { it.copy(error = null) }
            }
        }
    }

    private fun login(email: String, pass: String) {
        if (email.isBlank() || pass.isEmpty()) {
            _uiState.update { it.copy(error = UiText.Resource(Res.string.auth_fill_all_fields)) }
            return
        }
        viewModelScope.launch {
            _uiState.update { it.copy(isLoading = true, error = null) }

            val result = loginUseCase(email, pass)

            result.fold(
                onSuccess = { user ->
                    AppTelemetry.log(AnalyticsEvent.Login(AuthMethod.EMAIL))
                    checkIfEmailVerified(user)
                },
                onFailure = { error ->
                    _uiState.update { it.copy(isLoading = false, error = error.toUiText()) }
                }
            )
        }
    }

    private fun register(email: String, pass: String, name: String) {
        if (email.isBlank() || pass.isEmpty() || name.isBlank()) {
            _uiState.update { it.copy(error = UiText.Resource(Res.string.auth_fill_all_fields)) }
            return
        }
        viewModelScope.launch {
            _uiState.update { it.copy(isLoading = true, error = null) }

            val result = registerUserUseCase(email, pass, name)

            result.fold(
                onSuccess = { user ->
                    AppTelemetry.log(AnalyticsEvent.SignUp(AuthMethod.EMAIL))
                    _uiState.update { 
                        it.copy(
                            isLoading = false, 
                            user = user, 
                            requiresEmailVerification = true
                        ) 
                    }
                },
                onFailure = { error ->
                    _uiState.update { it.copy(isLoading = false, error = error.toUiText()) }
                }
            )
        }
    }
    
    private fun continueWithGoogle() {
        viewModelScope.launch {
            _uiState.update { it.copy(isLoading = true, error = null) }
            googleSignInUseCase().fold(
                // Google has verified the address: straight in, no verification screen.
                onSuccess = { user ->
                    if (user != null) {
                        AppTelemetry.log(AnalyticsEvent.Login(AuthMethod.GOOGLE))
                        onAuthSuccess(user)
                    } else _uiState.update { it.copy(isLoading = false) }
                },
                onFailure = { error ->
                    _uiState.update { it.copy(isLoading = false, error = error.toUiText()) }
                }
            )
        }
    }

    private fun continueWithApple() {
        viewModelScope.launch {
            _uiState.update { it.copy(isLoading = true, error = null) }
            appleSignInUseCase().fold(
                // Apple has verified the address (maybe a private-relay one): straight in.
                onSuccess = { user ->
                    if (user != null) {
                        AppTelemetry.log(AnalyticsEvent.Login(AuthMethod.APPLE))
                        onAuthSuccess(user)
                    } else _uiState.update { it.copy(isLoading = false) } // closed the sheet
                },
                onFailure = { error ->
                    _uiState.update { it.copy(isLoading = false, error = error.toUiText()) }
                }
            )
        }
    }

    private suspend fun checkIfEmailVerified(user: User) {
        val verificationResult = checkEmailVerificationUseCase()
        if (verificationResult.getOrNull() == true) {
            onAuthSuccess(user)
        } else {
             _uiState.update { 
                 it.copy(
                     isLoading = false, 
                     user = user, 
                     requiresEmailVerification = true,
                     error = UiText.Resource(Res.string.auth_verify_email)
                 ) 
             }
        }
    }
    
    private fun checkEmailVerification() {
        viewModelScope.launch {
            _uiState.update { it.copy(isLoading = true) }
            val verificationResult = checkEmailVerificationUseCase()
            if (verificationResult.getOrNull() == true) {
                _uiState.value.user?.let { user ->
                    onAuthSuccess(user)
                } ?: run {
                    _uiState.update { it.copy(isLoading = false, error = UiText.Resource(Res.string.error_session_expired), requiresEmailVerification = false) }
                }
            } else {
                _uiState.update { 
                    it.copy(
                        isLoading = false, 
                        error = UiText.Resource(Res.string.auth_email_not_verified)
                    ) 
                }
            }
        }
    }
    
    private fun resendVerificationEmail() {
        viewModelScope.launch {
            _uiState.update { it.copy(isLoading = true) }
            sendEmailVerificationUseCase().fold(
                onSuccess = {
                    _uiState.update { it.copy(isLoading = false, error = UiText.Resource(Res.string.auth_verification_sent)) }
                },
                onFailure = { e ->
                    _uiState.update { it.copy(isLoading = false, error = e.toUiText()) }
                }
            )
        }
    }
    
    private fun cancelVerification() {
        // Here we could technically call signout on the Auth provider but viewmodel doesn't have it.
        // We just reset the UI state.
        _uiState.update { 
            it.copy(
                user = null,
                requiresEmailVerification = false,
                error = null
            )
        }
    }

    private fun resetPassword(email: String) {
        viewModelScope.launch {
            _uiState.update { it.copy(isLoading = true) }
            resetPasswordUseCase(email)
                .onSuccess {
                    _uiState.update {
                        it.copy(
                            isLoading = false,
                            error = UiText.Resource(Res.string.auth_reset_sent)
                        )
                    }
                }
                .onFailure { e ->
                    _uiState.update { it.copy(isLoading = false, error = e.toUiText()) }
                }
        }
    }

    // Wspólna logika po sukcesie (Logowanie LUB Rejestracja)
    private suspend fun onAuthSuccess(user: User) {
        // 1. Zarejestruj token urządzenia (Push Notifications)
        val token = deviceInfoProvider.getFcmToken()
        if (token != null) {
            registerDeviceTokenUseCase(
                userId = user.uid,
                token = token,
                platform = deviceInfoProvider.getPlatform(),
                deviceName = deviceInfoProvider.getDeviceName(),
                language = deviceInfoProvider.getLanguage()
            )
        }

        // 2. Zaktualizuj stan
        _uiState.update {
            it.copy(
                isLoading = false,
                user = user,
                authSuccess = true
            )
        }
    }
}